/**
 * 2D camera over the iso world-screen plane (logical px).
 * (x, y) is the world point shown at the viewport centre; zoom is CSS px per
 * world px. The viewport is in CSS px; DPR is handled by the renderer
 * (resolution + autoDensity), so camera maths never sees device pixels
 * except for snapping (see `apply`).
 */
export class Camera {
    x = 0;
    y = 0;
    zoom = 1;
    minZoom;
    maxZoom;
    followRate;
    viewW = 1;
    viewH = 1;
    bounds = null;
    /** True while following a target; user panning turns it off. */
    following = true;
    constructor(opts = {}) {
        this.minZoom = opts.minZoom ?? 0.5;
        this.maxZoom = opts.maxZoom ?? 2.5;
        this.followRate = opts.followRate ?? 6;
    }
    setViewport(w, h) {
        this.viewW = Math.max(1, w);
        this.viewH = Math.max(1, h);
        this.clamp();
    }
    screenToWorld(sx, sy, out) {
        out.x = this.x + (sx - this.viewW / 2) / this.zoom;
        out.y = this.y + (sy - this.viewH / 2) / this.zoom;
        return out;
    }
    worldToScreen(wx, wy, out) {
        out.x = (wx - this.x) * this.zoom + this.viewW / 2;
        out.y = (wy - this.y) * this.zoom + this.viewH / 2;
        return out;
    }
    /** Visible world rect, optionally grown by `margin` world px on each side. */
    viewRect(margin, out) {
        const hw = this.viewW / 2 / this.zoom + margin;
        const hh = this.viewH / 2 / this.zoom + margin;
        out.left = this.x - hw;
        out.right = this.x + hw;
        out.top = this.y - hh;
        out.bottom = this.y + hh;
        return out;
    }
    /** Pans by a screen-space delta (finger/mouse movement). */
    panByScreen(dx, dy) {
        this.x -= dx / this.zoom;
        this.y -= dy / this.zoom;
        this.following = false;
        this.clamp();
    }
    /** Multiplies zoom by `factor`, keeping the world point under screen (sx, sy) fixed. */
    zoomAt(factor, sx, sy) {
        const z = Math.min(this.maxZoom, Math.max(this.minZoom, this.zoom * factor));
        if (z === this.zoom)
            return;
        // World point under the cursor before and after must match.
        const wx = this.x + (sx - this.viewW / 2) / this.zoom;
        const wy = this.y + (sy - this.viewH / 2) / this.zoom;
        this.zoom = z;
        this.x = wx - (sx - this.viewW / 2) / z;
        this.y = wy - (sy - this.viewH / 2) / z;
        this.clamp();
    }
    /** Frame-rate independent exponential follow toward (tx, ty). */
    follow(tx, ty, dt) {
        if (!this.following)
            return;
        const k = 1 - Math.exp(-this.followRate * dt);
        this.x += (tx - this.x) * k;
        this.y += (ty - this.y) * k;
        this.clamp();
    }
    /** Jumps to a point (no smoothing). */
    centerOn(x, y) {
        this.x = x;
        this.y = y;
        this.clamp();
    }
    /**
     * Keeps the view inside `bounds`. If the view is larger than the bounds on
     * an axis, the bounds are centred on that axis instead.
     */
    clamp() {
        const b = this.bounds;
        if (!b)
            return;
        const hw = this.viewW / 2 / this.zoom;
        const hh = this.viewH / 2 / this.zoom;
        if (b.right - b.left <= hw * 2)
            this.x = (b.left + b.right) / 2;
        else
            this.x = Math.min(b.right - hw, Math.max(b.left + hw, this.x));
        if (b.bottom - b.top <= hh * 2)
            this.y = (b.top + b.bottom) / 2;
        else
            this.y = Math.min(b.bottom - hh, Math.max(b.top + hh, this.y));
    }
    /**
     * Writes the transform to a container. The translation is snapped to the
     * device-pixel grid (`resolution` = renderer resolution) so static art
     * does not shimmer while the camera glides.
     */
    apply(target, resolution) {
        const tx = this.viewW / 2 - this.x * this.zoom;
        const ty = this.viewH / 2 - this.y * this.zoom;
        target.position.set(Math.round(tx * resolution) / resolution, Math.round(ty * resolution) / resolution);
        target.scale.set(this.zoom, this.zoom);
    }
}
